import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@orbin/ui-web";
import { Check } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { CardFormData } from "../lib/mercadopago";
import { loadMercadoPago } from "../lib/mercadopago";
import { formatBrl, needsPayerIdentification } from "../lib/metering";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";
import { PaymentAmountPicker } from "./PaymentAmountPicker";
import { TaxIdField } from "./TaxIdField";

/**
 * Pays by card. The provider's own form tokenizes the card inside its frame, so the number
 * never reaches this app or the server; only the token is sent.
 */
export function CardCheckout({
  publicKey,
  amounts,
  fixedAmountCents,
  onPaid,
}: {
  publicKey: string;
  amounts: readonly number[];
  /** A link already fixed the amount, so the form opens straight away. */
  fixedAmountCents?: number;
  onPaid?: () => void;
}) {
  const { t, i18n } = useLingui();
  const containerId = useId().replaceAll(":", "");
  const [amount, setAmount] = useState(fixedAmountCents ?? amounts[1] ?? amounts[0] ?? 0);
  const [started, setStarted] = useState(fixedAmountCents !== undefined);
  const [needsTaxId, setNeedsTaxId] = useState(false);
  const [ready, setReady] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nonce = useRef(crypto.randomUUID());
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  useEffect(() => {
    if (!started) return;
    let active = true;
    let controller: { unmount: () => void } | undefined;
    loadMercadoPago()
      .then(async (MercadoPago) => {
        const mp = new MercadoPago(publicKey, { locale: "pt-BR" });
        const created = await mp.bricks().create("cardPayment", containerId, {
          initialization: { amount: amount / 100 },
          callbacks: {
            onReady: () => active && setReady(true),
            onError: () => active && setError(t`The card form could not load`),
            onSubmit: async (data: CardFormData) => {
              setError(null);
              try {
                const charge = await rpc.payments.createCardCharge({
                  amountCents: amount,
                  cardToken: data.token,
                  paymentMethodId: data.payment_method_id,
                  ...(data.issuer_id ? { issuerId: String(data.issuer_id) } : {}),
                  installments: data.installments ?? 1,
                  clientNonce: `${nonce.current}-${amount}`,
                });
                if (charge.status === "paid") {
                  setPaid(true);
                  onPaidRef.current?.();
                } else {
                  setError(
                    charge.status === "pending"
                      ? t`The payment is being confirmed`
                      : t`The payment was declined`,
                  );
                  nonce.current = crypto.randomUUID();
                }
              } catch (cause) {
                nonce.current = crypto.randomUUID();
                if (needsPayerIdentification(cause)) setNeedsTaxId(true);
                else setError(errorText(cause, t`The payment could not be made`));
                throw cause;
              }
            },
          },
        });
        if (active) controller = created;
        else created.unmount();
      })
      .catch(() => active && setError(t`The card form could not load`));
    return () => {
      active = false;
      controller?.unmount();
    };
  }, [started, amount, publicKey, containerId, t]);

  if (paid) {
    return (
      <div role="status" data-testid="card-paid" className="flex flex-col items-center gap-3 py-6">
        <span className="flex size-10 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="size-5" aria-hidden="true" />
        </span>
        <p className="text-body1 font-medium text-foreground">
          <Trans>Payment received</Trans>
        </p>
        <p className="text-sm text-muted-foreground">{formatBrl(amount, i18n.locale || "en")}</p>
      </div>
    );
  }

  return (
    <div data-testid="card-checkout" className="flex flex-col gap-4">
      {started ? null : (
        <>
          <PaymentAmountPicker amounts={amounts} value={amount} onChange={setAmount} />
          <Button onClick={() => setStarted(true)}>
            <Trans>Pay by card</Trans>
          </Button>
        </>
      )}
      {started ? (
        <>
          <div id={containerId} aria-busy={!ready} className="min-h-24" />
          {fixedAmountCents === undefined ? (
            <Button variant="ghost" size="sm" onClick={() => setStarted(false)}>
              <Trans>Change amount</Trans>
            </Button>
          ) : null}
          {needsTaxId ? <TaxIdField onSaved={() => setNeedsTaxId(false)} /> : null}
        </>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
